import { useState, useEffect, useContext } from 'react';
import { apiFetch, useAuth } from "../../../context/AuthContext";
import { useProfile } from "../../../hooks/useProfile";
import { ThemeContext } from "../../../constant/theme";
import { LanguageContext } from "../../../constant/LanguageContext";
import { getScreenSize, getResponsiveSpacing, getResponsiveFontSizes } from "../utils/responsiveUtils";
import { getEscalationStyles } from "../utils/escalationStyles";
import { text } from "../constants/escalationConstants";

export const useEscalationGroupMapping = () => {
    const apiBaseUrl = process.env.REACT_APP_BASE_URL;

    const { theme } = useContext(ThemeContext);
    const { language } = useContext(LanguageContext);

    const [screenSize, setScreenSize] = useState(getScreenSize());
    const [spacing, setSpacing] = useState(getResponsiveSpacing(screenSize));
    const [fonts, setFonts] = useState(getResponsiveFontSizes(screenSize));

    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(false);
    const [mappings, setMappings] = useState([]);
    const [mappingsLoading, setMappingsLoading] = useState(false);
    const { profile, profileLoading } = useProfile();
    const userRole = profile?.role ?? "";
    const { hasFeatureAccess } = useAuth();
    // Was previously hardcoded to `userRole === "admin" || "superadmin"`,
    // which permanently blocked promoted Admin View accounts (DB role
    // "user") regardless of any admin-level grant. hasFeatureAccess already
    // bypasses true for real admin/superadmin, so this covers both.
    const canMapEscalation = hasFeatureAccess("complaints", "mapEscalation");

    const [alertModal, setAlertModal] = useState({
        open: false,
        title: '',
        message: '',
        variant: 'info',
    });

    const [formData, setFormData] = useState({
        escalation_type: "",
        issuerGroupId: "",
        priority: "",
        assignedGroupId: ""
    });

    const [formErrors, setFormErrors] = useState({});

    const t = text[language];
    const styles = getEscalationStyles({ theme, language, screenSize, spacing, fonts });

    useEffect(() => {
        const handleResize = () => {
            const newScreenSize = getScreenSize();
            setScreenSize(newScreenSize);
            setSpacing(getResponsiveSpacing(newScreenSize));
            setFonts(getResponsiveFontSizes(newScreenSize));
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        if (canMapEscalation) {
            fetchGroups();
            fetchMappings();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canMapEscalation]);

    const fetchMappings = async () => {
        try {
            setMappingsLoading(true);
            const res = await apiFetch(`${apiBaseUrl}/api/escalations/mappings`, { method: "GET" });
            if (!res.ok) throw new Error("Failed to load mappings");
            const data = await res.json();
            setMappings(Array.isArray(data?.data) ? data.data : []);
        } catch (err) {
            console.error('Error fetching escalation mappings:', err);
            // Non-blocking: the create form still works if the list fails.
            setMappings([]);
        } finally {
            setMappingsLoading(false);
        }
    };

    const handleDeleteMapping = async (id) => {
        try {
            setMappingsLoading(true);
            const res = await apiFetch(`${apiBaseUrl}/api/escalations/mappings/${id}`, { method: "DELETE" });
            if (!res.ok) throw new Error("Failed to delete mapping");
            setAlertModal({
                open: true,
                title: t.deleteMappingSuccess,
                message: t.deleteMappingSuccess,
                variant: 'success',
            });
            await fetchMappings();
        } catch (err) {
            console.error('Error deleting escalation mapping:', err);
            setAlertModal({
                open: true,
                title: t.deleteMappingFailed,
                message: t.deleteMappingFailed,
                variant: 'error',
            });
            setMappingsLoading(false);
        }
    };

    const fetchGroups = async () => {
        try {
            setLoading(true);
            const res = await apiFetch(`${apiBaseUrl}/api/groups/`, { method: "GET" });
            const data = await res.json();
            setGroups(data || []);
        } catch (err) {
            console.error('Error fetching groups:', err);
            setAlertModal({
                open: true,
                title: text[language].mappingFailed,
                message: "Failed to load groups",
                variant: 'error',
            });
        } finally {
            setLoading(false);
        }
    };

    const validateForm = () => {
        const errors = {};

        if (!formData.escalation_type) {
            errors.escalation_type = t.typeRequired;
        }
        if (!formData.issuerGroupId) {
            errors.issuerGroupId = t.issuerGroupRequired;
        }
        if (!formData.priority) {
            errors.priority = t.priorityRequired;
        }
        if (!formData.assignedGroupId) {
            errors.assignedGroupId = t.assignedGroupRequired;
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!canMapEscalation) {
            setAlertModal({
                open: true,
                title: t.adminOnly,
                message: t.unauthorized,
                variant: 'error',
            });
            return;
        }

        if (!validateForm()) {
            return;
        }

        try {
            setLoading(true);
            const res = await apiFetch(`${apiBaseUrl}/api/escalations/map-escalation`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(formData)
            });

            if (!res.ok) throw new Error("Failed to create mapping");

            setAlertModal({
                open: true,
                title: t.mappingSuccess,
                message: t.mappingSuccess,
                variant: 'success',
            });

            setFormData({
                escalation_type: "",
                issuerGroupId: "",
                priority: "",
                assignedGroupId: ""
            });
            setFormErrors({});
            fetchMappings();
        } catch (err) {
            console.error('Mapping error:', err);
            setAlertModal({
                open: true,
                title: t.mappingFailed,
                message: t.mappingFailed,
                variant: 'error',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        if (formErrors[field]) {
            setFormErrors(prev => ({
                ...prev,
                [field]: ""
            }));
        }
    };

    const getGroupById = (groupId) => {
        return groups.find(g => g.groupId === groupId);
    };

    const resetForm = () => {
        setFormData({
            escalation_type: "",
            issuerGroupId: "",
            priority: "",
            assignedGroupId: ""
        });
        setFormErrors({});
    };

    return {
        theme,
        language,
        screenSize,
        fonts,
        groups,
        loading,
        mappings,
        mappingsLoading,
        handleDeleteMapping,
        profileLoading,
        userRole,
        canMapEscalation,
        alertModal,
        setAlertModal,
        formData,
        formErrors,
        styles,
        t,
        handleSubmit,
        handleInputChange,
        getGroupById,
        resetForm,
    };
};